import React from 'react';
import clsx from 'clsx';
import { Dropdown } from '@shared/components';

import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import DisabledPreviousPageIcon from '@assets/icons/next-disabled.svg';
import NextPageIcon from '@assets/icons/next.svg';
import SelectIcon from '@assets/icons/down-outline-arrow-grey.svg';

import useStyles from './style';
import { IconButton } from '@material-ui/core';

const noop = () => {};

const Pagination = ({
  classes: passedClasses = {},
  className = '',
  disabled = false,
  elements = [],
  hiddenCount = null,
  onPageNumberChange = noop,
  onPageSizeChange = noop,
  pageNumber = 1,
  pageSize = 5,
  rowCount = 5,
  total = 5,
}) => {
  const [state, setState] = React.useState({
    inputValue: '',
    error: null,
  });
  const classes = useStyles();
  const lastPage = React.useMemo(() => {
    if (pageSize === -1) {
      return 1;
    }
    return Math.ceil(total / pageSize);
  }, [pageSize, total]);

  React.useEffect(() => {
    if (pageSize === -1) {
      onPageSizeChange(5);
    }
    return () => {
      setState({
        inputValue: '',
        error: '',
      });
    };
  }, []);

  React.useEffect(() => {
    if (pageNumber !== 1 && pageNumber !== lastPage && !state.inputValue) {
      setState(prevState => ({
        inputValue: pageNumber,
        error: null,
      }));
    }
    // eslint-disable-next-line
  }, [pageNumber, lastPage]);

  const goToFirstPage = () => {
    if (pageNumber === 1) {
      return;
    }

    onPageNumberChange(1);
  };

  const goToLastPage = () => {
    if (pageNumber === lastPage) {
      return;
    }

    onPageNumberChange(lastPage);
  };

  const goToNextPage = () => {
    if (pageNumber === lastPage) {
      return;
    }

    onPageNumberChange(++pageNumber);
  };

  const goToPreviousPage = () => {
    if (pageNumber === 1) {
      return;
    }

    onPageNumberChange(--pageNumber);
  };

  if (total === 0) {
    return <></>;
  }

  return (
    <div
      className={clsx(
        'd-flex f-align-center f-justify-between pt-2 pr-3',
        classes.wrapper,
        className,
        passedClasses.root,
        {
          disabled: disabled,
        }
      )}
      data-testid='pagination-wrapper'
    >
      <div
        className={clsx(
          'd-flex f-align-center',
          classes.controlWrapper,
          passedClasses.controlWrapper
        )}
        data-testid='pagination-control-wrapper'
      >
        <Paper className={clsx('mr-1', classes.nextButton)} elevation={1}>
          <IconButton
            size='small'
            onClick={goToPreviousPage}
            disabled={pageNumber === 1 || lastPage === 1}
            className={clsx({
              'bg-default': pageNumber === 1 || lastPage === 1,
            })}
          >
            <img
              size='small'
              alt='Go to previous page'
              className={clsx(classes.invertedIcon, 'p-1')}
              src={
                pageNumber === 1 || lastPage === 1
                  ? DisabledPreviousPageIcon
                  : NextPageIcon
              }
            />
          </IconButton>
        </Paper>
        <div
          className={clsx('d-flex ml-4 mr-1', passedClasses.pageNumberWrapper)}
          data-testid='page-number-wrapper'
        >
          {pageNumber !== 1 && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(1)}
            >
              1
            </Typography>
          )}

          {pageNumber > 6 && (
            <Typography variant='body1' className='mr-4'>
              ...
            </Typography>
          )}

          {pageNumber - 4 > 1 && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber - 4)}
            >
              {pageNumber - 4}
            </Typography>
          )}

          {pageNumber - 3 > 1 && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber - 3)}
            >
              {pageNumber - 3}
            </Typography>
          )}

          {pageNumber - 2 > 1 && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber - 2)}
            >
              {pageNumber - 2}
            </Typography>
          )}

          {pageNumber - 1 > 1 && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber - 1)}
            >
              {pageNumber - 1}
            </Typography>
          )}

          <Typography
            variant='body1'
            className='mr-4 bold-font'
            // onClick={() => onPageNumberChange(pageNumber)}
            // disabled
            color='primary'
          >
            {pageNumber}
          </Typography>

          {pageNumber + 1 < lastPage && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber + 1)}
            >
              {pageNumber + 1}
            </Typography>
          )}

          {pageNumber + 2 < lastPage && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber + 2)}
            >
              {pageNumber + 2}
            </Typography>
          )}

          {pageNumber + 3 < lastPage && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber + 3)}
            >
              {pageNumber + 3}
            </Typography>
          )}

          {pageNumber + 4 < lastPage && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(pageNumber + 4)}
            >
              {pageNumber + 4}
            </Typography>
          )}

          {pageNumber < lastPage - 5 && (
            <Typography variant='body1' className='mr-4'>
              ...
            </Typography>
          )}

          {pageNumber !== lastPage && (
            <Typography
              variant='body1'
              className='mr-4 c-pointer'
              onClick={() => onPageNumberChange(lastPage)}
            >
              {lastPage}
            </Typography>
          )}
        </div>
        <Paper className={clsx('mr-1', classes.nextButton)} elevation={1}>
          <IconButton
            size='small'
            variant='contained'
            disabled={lastPage === 1 || pageNumber === lastPage}
            onClick={goToNextPage}
            className={clsx({
              'bg-default': lastPage === 1 || pageNumber === lastPage,
            })}
          >
            <img
              size='small'
              alt='Go to next page'
              className='p-1'
              src={
                lastPage === 1 || pageNumber === lastPage
                  ? DisabledPreviousPageIcon
                  : NextPageIcon
              }
            />
          </IconButton>
        </Paper>
      </div>
      <div className='d-flex flex-1' data-testid='pagination-element'>
        {elements.map(element => element)}
      </div>
      <div className='d-flex'>
        <div className='d-flex mr-5' data-testid='pagination-dropdown'>
          <Typography className='mr-2 color-text-primary' variant='body2'>
            Show:
          </Typography>
          <Dropdown
            customToggle={({ anchorRef, onClick }) => (
              <span
                ref={anchorRef}
                onClick={onClick}
                className='d-flex f-align-end c-pointer'
              >
                <Typography
                  className='color-text-primary bold-font'
                  variant='body2'
                >
                  {pageSize === -1 ? 'Show All' : `${pageSize} Rows`}
                </Typography>
                <img
                  alt='selectUp'
                  className={clsx('p-relative mr-5 ml-3', classes.pageSizeIcon)}
                  src={SelectIcon}
                />
              </span>
            )}
            onChange={selection => {
              onPageSizeChange(parseInt(selection.value));
            }}
            classes={{
              paper: classes.pageSizeDropdown,
            }}
            options={[
              {
                label: '5 Rows',
                value: '5',
                isDisabled: pageSize === 5,
                isSelected: pageSize === 5,
              },
              {
                label: '10 Rows',
                value: '10',
                isDisabled: pageSize === 10,
                isSelected: pageSize === 10,
              },
              {
                label: '20 Rows',
                value: '20',
                isDisabled: pageSize === 20,
                isSelected: pageSize === 20,
              },
              {
                label: '30 Rows',
                value: '30',
                isDisabled: pageSize === 30,
                isSelected: pageSize === 30,
              },
              {
                label: '40 Rows',
                value: '40',
                isDisabled: pageSize === 40,
                isSelected: pageSize === 40,
              },
              {
                label: '50 Rows',
                value: '50',
                isDisabled: pageSize === 50,
                isSelected: pageSize === 50,
              },
              {
                label: '100 Rows',
                value: '100',
                isDisabled: pageSize === 100,
                isSelected: pageSize === 100,
              },
              {
                label: '150 Rows',
                value: '150',
                isDisabled: pageSize === 150,
                isSelected: pageSize === 150,
              },
              {
                label: '200 Rows',
                value: '200',
                isDisabled: pageSize === 200,
                isSelected: pageSize === 200,
              },
              {
                label: '250 Rows',
                value: '250',
                isDisabled: pageSize === 250,
                isSelected: pageSize === 250,
              },
              {
                label: '300 Rows',
                value: '300',
                isDisabled: pageSize === 300,
                isSelected: pageSize === 300,
              },
              // {
              //   label: 'Show All',
              //   value: '-1',
              //   isDisabled: pageSize === -1,
              //   isSelected: pageSize === -1,
              // },
            ]}
            placement='top-end'
          />
        </div>
        <div data-testid='pagination-entries'>
          <div className='d-flex f-align-end'>
            <Typography className='color-text-primary' variant='body2'>
              Total items:
            </Typography>
            <Typography
              color='primary'
              className='bold-font ml-2'
              variant='body2'
            >
              {total}
            </Typography>
          </div>
        </div>
        {hiddenCount !== null && (
          <div className='ml-3' data-testid='pagination-hidden-count'>
            <Typography className='color-text-primary' variant='caption'>
              Hidden: {hiddenCount}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
